import { z } from 'zod';
import { id, organizationId } from './common.js';
import { groupStatus } from './group.js';
import { role, scopeType, membershipStatus } from './membership.js';

/** Dental-side office roles. Platform ownership/admin roles remain in Layer 0. */
export const officeAdminRole = z.enum(['group_admin', 'location_manager', 'credential_reviewer']);
export type OfficeAdminRole = z.infer<typeof officeAdminRole>;

export const officeAccountUser = z.object({
  userId: id,
  email: z.string().email(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  roles: z.array(role),
});
export type OfficeAccountUser = z.infer<typeof officeAccountUser>;

export const officePracticeSummary = z.object({
  id,
  name: z.string().min(1),
  status: groupStatus,
  locationCount: z.number().int().nonnegative(),
  locations: z.array(z.object({ id, name: z.string().min(1) })),
  editable: z.boolean(),
});
export type OfficePracticeSummary = z.infer<typeof officePracticeSummary>;

export const officeAccountOverview = z.object({
  currentUser: officeAccountUser,
  organization: z.object({
    id: organizationId,
    name: z.string().min(1),
  }),
  practices: z.array(officePracticeSummary),
  permissions: z.object({
    canEditProfile: z.boolean(),
    editablePracticeIds: z.array(id),
    canManageRoles: z.boolean(),
  }),
});
export type OfficeAccountOverview = z.infer<typeof officeAccountOverview>;

export const updateOfficeAccountUserInput = z
  .object({
    firstName: z.string().trim().min(1).max(100).optional(),
    lastName: z.string().trim().min(1).max(100).optional(),
  })
  .refine((value) => value.firstName !== undefined || value.lastName !== undefined, {
    message: 'Provide at least one profile field',
  });
export type UpdateOfficeAccountUserInput = z.infer<typeof updateOfficeAccountUserInput>;

export const officeMember = z.object({
  /** Null for the current Layer 0 platform administrator, which is not duplicated in D1. */
  membershipId: id.nullable(),
  userId: id,
  email: z.string().email().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  role,
  scopeType,
  scopeId: id.nullable(),
  scopeName: z.string().nullable(),
  status: membershipStatus,
  isCurrentUser: z.boolean(),
  canManage: z.boolean(),
});
export type OfficeMember = z.infer<typeof officeMember>;

export const officeMemberListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().trim().max(100).default(''),
  role: officeAdminRole.or(z.literal('all')).default('all'),
  status: membershipStatus.or(z.literal('all')).default('active'),
});
export type OfficeMemberListQuery = z.infer<typeof officeMemberListQuery>;

export const officeMemberPage = z.object({
  items: z.array(officeMember),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
});
export type OfficeMemberPage = z.infer<typeof officeMemberPage>;

export const updateOfficeMemberInput = z
  .object({
    role: officeAdminRole.optional(),
    status: membershipStatus.optional(),
  })
  .refine((value) => value.role !== undefined || value.status !== undefined, {
    message: 'Provide a role or status change',
  });
export type UpdateOfficeMemberInput = z.infer<typeof updateOfficeMemberInput>;
